// frontend/src/hooks/useApi.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { Project, Conversation, Message, MiniAppType, MESSAGE_ATTACHMENT_MAX_BYTES, SubscriptionTier } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { dbSelect, dbInsert, dbUpdate, dbDelete, invokeFunction, uploadFile } from '../lib/api';
import { KimiOutputFilterStream, filterKimiOutput, shouldFilterKimiOutput } from '../utils/kimiOutputFilter';
import { invalidateUsageCache } from './useUsage';
import { invalidateAnonymousCreditsCache } from './useAnonymousCredits';

const MAX_MESSAGE_ATTACHMENTS = 8;
const PENDING_AGENT_RUN_PREFIX = 'llambada.pending-agent-run.';
const BACKGROUND_RECOVERY_STATUS = 'Connection lost. Continuing in background...';

type AttachedFilePayload = {
  name: string;
  mimeType: string;
  size: number;
  contentBase64: string;
};

type PendingAgentRunRecord = {
  conversationId: string;
  userMessageId: string;
  startedAt: string;
  partialAssistantContent?: string;
  streamingStatus?: string;
  streamUpdatedAt?: string;
};

type PersistedAgentStreamSnapshot = {
  status: string;
  content: string;
  streamingStatus: string;
  errorMessage: string | null;
  updatedAt: string | null;
};

type ParsedAttachmentLabels = {
  mainContent: string;
  attachmentNames: string[];
};

type MessageAttachmentRecord = {
  id: string;
  message_id: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  storage_path: string;
  created_at: string;
};

type MessageAttachmentInsert = {
  message_id: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  storage_path: string;
};

function sanitizeAttachmentFilename(filename: string): string {
  const trimmed = filename.trim();
  const normalized = trimmed
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || 'attachment';
}

function base64ToBlob(contentBase64: string, mimeType: string): Blob {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType || 'application/octet-stream' });
}

function splitTrailingAttachmentLabels(content: string): ParsedAttachmentLabels {
  const attachmentRegex = /\n?\n?(\[Attached: [^\]]+\]\n?)+$/;
  const attachmentMatch = content.match(attachmentRegex);
  if (!attachmentMatch) {
    return { mainContent: content, attachmentNames: [] };
  }

  const mainContent = content.slice(0, attachmentMatch.index).trimEnd();
  const attachmentNames: string[] = [];
  const nameRegex = /\[Attached: ([^\]]+)\]/g;
  let match: RegExpExecArray | null;
  while ((match = nameRegex.exec(attachmentMatch[0])) !== null) {
    attachmentNames.push(match[1]);
  }

  return { mainContent, attachmentNames };
}

function getPendingAgentRunKey(conversationId: string): string {
  return `${PENDING_AGENT_RUN_PREFIX}${conversationId}`;
}

function readPendingAgentRun(conversationId?: string): PendingAgentRunRecord | null {
  if (!conversationId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(getPendingAgentRunKey(conversationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingAgentRunRecord;
    if (!parsed?.conversationId || !parsed?.userMessageId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePendingAgentRun(record: PendingAgentRunRecord): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(getPendingAgentRunKey(record.conversationId), JSON.stringify(record));
  } catch {
    // Ignore localStorage failures (e.g., private mode quota restrictions)
  }
}

function upsertPendingAgentRun(
  conversationId: string | undefined,
  updates: Partial<PendingAgentRunRecord>,
): PendingAgentRunRecord | null {
  if (!conversationId) return null;

  const existing = readPendingAgentRun(conversationId);
  const merged: PendingAgentRunRecord = {
    conversationId,
    userMessageId: updates.userMessageId ?? existing?.userMessageId ?? '',
    startedAt: updates.startedAt ?? existing?.startedAt ?? new Date().toISOString(),
    partialAssistantContent: updates.partialAssistantContent ?? existing?.partialAssistantContent,
    streamingStatus: updates.streamingStatus ?? existing?.streamingStatus,
    streamUpdatedAt: updates.streamUpdatedAt ?? existing?.streamUpdatedAt,
  };

  if (!merged.userMessageId) return existing;
  writePendingAgentRun(merged);
  return merged;
}

function clearPendingAgentRun(conversationId?: string): void {
  if (!conversationId || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(getPendingAgentRunKey(conversationId));
  } catch {
    // Ignore localStorage failures
  }
}

function hasAssistantAfterUserMessage(messageList: Message[], userMessageId: string): boolean {
  const userMessageIndex = messageList.findIndex((msg) => msg.id === userMessageId);
  if (userMessageIndex === -1) return false;
  return messageList.slice(userMessageIndex + 1).some((msg) => msg.role === 'assistant');
}

function isTransientConnectionError(message: string): boolean {
  const normalized = message.toLowerCase();
  return normalized.includes('failed to fetch') ||
    normalized.includes('networkerror') ||
    normalized.includes('network request failed') ||
    normalized.includes('the network connection was lost') ||
    normalized.includes('connection was lost') ||
    normalized.includes('load failed') ||
    normalized.includes('request was terminated') ||
    normalized.includes('response stream') ||
    normalized.includes('run did not complete');
}

function blobToBase64(blob: Blob, label = 'blob'): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Failed to read file: ${label}`));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error(`Unexpected file read result for: ${label}`));
        return;
      }
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

async function serializeAttachedFiles(files: File[], subscriptionTier: SubscriptionTier = 'free'): Promise<AttachedFilePayload[]> {
  if (files.length > MAX_MESSAGE_ATTACHMENTS) {
    throw new Error(`You can attach up to ${MAX_MESSAGE_ATTACHMENTS} files per message.`);
  }

  const maxBytes = MESSAGE_ATTACHMENT_MAX_BYTES[subscriptionTier];
  const maxMB = Math.round(maxBytes / (1024 * 1024));

  const serialized: AttachedFilePayload[] = [];
  for (const file of files) {
    if (file.size > maxBytes) {
      throw new Error(`File "${file.name}" is too large. Max file size is ${maxMB} MB.`);
    }
    const contentBase64 = await blobToBase64(file, file.name);
    serialized.push({
      name: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
      contentBase64,
    });
  }
  return serialized;
}

export function useProjects() {
  const { user, session } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    if (!user || !session) {
      setProjects([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await dbSelect<Project[]>('projects', {
        token: session.access_token,
        filters: { user_id: `eq.${user.id}` },
        order: 'updated_at.desc',
      });

      if (fetchError) throw fetchError;
      setProjects(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, [user, session]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const createProject = async (name: string, description?: string) => {
    if (!user || !session) return { error: 'Not authenticated' };

    // First, create the project in database
    const { data, error } = await dbInsert<Project>('projects',
      { user_id: user.id, name, description },
      { token: session.access_token }
    );

    if (error || !data) return { error: error?.message || 'Failed to create project' };

    // Initialize default code files
    let initError: string | null = null;
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const initResponse = await fetch(
        `${supabaseUrl}/functions/v1/project-code?projectId=${data.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'init' }),
        }
      );
      if (!initResponse.ok) {
        initError = 'Failed to initialize project files';
        console.error('Failed to initialize project files');
      }
    } catch (err) {
      initError = err instanceof Error ? err.message : 'Failed to initialize project files';
      console.error('Error initializing project files:', err);
    }

    // Then, create the Cloud Run infrastructure
    let cloudRunError: string | null = null;
    try {
      const { data: result, error: fnError } = await invokeFunction<{ serviceName: string; serviceUrl: string }>(
        'create-project-infrastructure',
        { projectId: data.id, projectName: name },
        { token: session.access_token }
      );

      if (fnError) {
        cloudRunError = fnError.message || 'Failed to create Cloud Run infrastructure';
        console.error('Failed to create Cloud Run infrastructure:', cloudRunError);
      } else if (result) {
        data.cloud_run_job_name = result.serviceName;
        data.cloud_run_service_url = result.serviceUrl;
      }
    } catch (err) {
      cloudRunError = err instanceof Error ? err.message : 'Failed to create Cloud Run infrastructure';
      console.error('Error creating Cloud Run infrastructure:', err);
    }

    setProjects((prev) => [data, ...prev]);

    return { data, cloudRunError: initError || cloudRunError };
  };

  const setupProjectInfrastructure = async (projectId: string, projectName: string) => {
    if (!session) return { error: 'Not authenticated' };

    try {
      const { data: result, error: fnError } = await invokeFunction<{ serviceName: string; serviceUrl: string }>(
        'create-project-infrastructure',
        { projectId, projectName },
        { token: session.access_token }
      );

      if (fnError) {
        return { error: fnError.message || 'Failed to create project infrastructure' };
      }

      if (result) {
        setProjects((prev) =>
          prev.map((p) =>
            p.id === projectId ? { ...p, cloud_run_job_name: result.serviceName, cloud_run_service_url: result.serviceUrl } : p
          )
        );
        return { data: result };
      }

      return { error: 'No result returned' };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Failed to create project infrastructure' };
    }
  };

  const updateProject = async (id: string, updates: {
    name?: string;
    description?: string;
    specification?: string | null;
    logo_emoji?: string | null;
    type?: MiniAppType | null;
    is_published?: boolean;
    required_tier?: string | null;
    monetization_mode?: string;
    requires_auth?: boolean;
  }) => {
    if (!session) return { error: 'Not authenticated' };

    const { data, error } = await dbUpdate<Project>(
      'projects',
      updates,
      { id: `eq.${id}` },
      { token: session.access_token }
    );

    if (error) return { error: error.message };

    if (updates.is_published === true) {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const syncResponse = await fetch(
          `${supabaseUrl}/functions/v1/project-code?projectId=${id}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'sync-backend-infra-on-publish' }),
          }
        );
        if (!syncResponse.ok) {
          const syncError = await syncResponse.json().catch(() => null);
          return { error: syncError?.error || 'Published, but failed to sync backend infrastructure' };
        }
      } catch (err) {
        return { error: err instanceof Error ? err.message : 'Published, but failed to sync backend infrastructure' };
      }
    }

    if (data) {
      setProjects((prev) =>
        prev.map((p) => (p.id === id ? data : p))
      );
    }
    return { data };
  };

  const deleteProject = async (id: string) => {
    if (!session) return { error: 'Not authenticated' };

    try {
      // Call the delete-project-infrastructure edge function
      // This deletes: GCS files, Cloud Run service, Artifact Registry images,
      // Supabase Storage files, and finally the database record
      const { data, error: fnError } = await invokeFunction<{ success: boolean; error?: string; details?: unknown }>(
        'delete-project-infrastructure',
        { projectId: id },
        { token: session.access_token }
      );

      if (fnError) {
        return { error: fnError.message || 'Failed to delete project' };
      }

      if (!data?.success) {
        return { error: data?.error || 'Failed to delete project' };
      }

      setProjects((prev) => prev.filter((p) => p.id !== id));
      return { success: true, details: data.details };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Failed to delete project' };
    }
  };

  const forkProject = async (sourceProjectId: string, name: string, description?: string) => {
    if (!user || !session) return { error: 'Not authenticated' };

    // First, create the forked project in database
    const { data, error } = await dbInsert<Project>('projects',
      { user_id: user.id, name, description, forked_from: sourceProjectId },
      { token: session.access_token }
    );

    if (error || !data) return { error: error?.message || 'Failed to create forked project' };

    // Copy code files from source project
    let forkError: string | null = null;
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const forkResponse = await fetch(
        `${supabaseUrl}/functions/v1/project-code?projectId=${data.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'fork', sourceProjectId }),
        }
      );
      if (!forkResponse.ok) {
        forkError = 'Failed to copy project files';
        console.error('Failed to fork project files');
      }
    } catch (err) {
      forkError = err instanceof Error ? err.message : 'Failed to copy project files';
      console.error('Error forking project files:', err);
    }

    // Forked projects don't need their own Cloud Run infrastructure
    // They use the original project's container

    setProjects((prev) => [data, ...prev]);

    return { data, forkError };
  };

  return {
    projects,
    loading,
    error,
    fetchProjects,
    createProject,
    updateProject,
    deleteProject,
    forkProject,
    setupProjectInfrastructure,
  };
}

// Streaming state for mini-app execution
// Shared types for mini-app execution
export interface FormDataEntry {
  key: string;
  type: 'file' | 'string';
  filename?: string;
  contentType?: string;
  data: number[] | string;
}

export interface StreamingState {
  status: 'idle' | 'authenticating' | 'running' | 'completed' | 'failed';
  progress?: { percent: number; message: string };
  outputFiles: Array<{ filename: string; size: number; content?: string }>;
  error?: string;
  errorCode?: string;
  logs?: {
    stdout: string;
    stderr: string;
    exitCode: number;
    hasAppError?: boolean; // True if application-level error was detected in output
  };
  streamingLogs?: {
    stdout: string;
    stderr: string;
  };
}

// Shared hook for running mini-apps (used by both authenticated and public pages)
// getTurnstileToken: Optional function to get Turnstile token for bot protection
export function useMiniAppRunner(
  projectId: string | undefined,
  accessToken?: string,
  getTurnstileToken?: () => Promise<string | null>
) {
  const [streaming, setStreaming] = useState<StreamingState>({
    status: 'idle',
    outputFiles: [],
  });
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const refreshCreditDisplays = useCallback(() => {
    // Run immediately, then retry a few times because save-run-cost is async
    // and may commit credit deductions shortly after stream completion.
    const refresh = () => {
      invalidateUsageCache();
      invalidateAnonymousCreditsCache();
    };

    refresh();
    [1200, 3000, 7000].forEach((delayMs) => {
      setTimeout(refresh, delayMs);
    });
  }, []);

  const runMiniApp = useCallback(async (entries: FormDataEntry[]) => {
    if (!projectId) return { error: 'Not ready' };

    // Acquire a Web Lock to prevent the browser from freezing this tab
    // while the SSE stream is active.  The lock is released automatically
    // when the callback resolves (stream completes, fails, or disconnects).
    const runWithLock = async (): Promise<{ error?: string; errorCode?: string; success?: boolean }> => {

    try {
      setStreaming({ status: 'authenticating', outputFiles: [], streamingLogs: { stdout: '', stderr: '' } });
      setError(null);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      // 1. Log the run usage IF the user is authenticated (Revenue Sharing Logic)
      if (user && accessToken) {
        const { error: logError } = await dbInsert('app_usage',
          { project_id: projectId, user_id: user.id },
          { token: accessToken, returning: false }
        );

        if (logError) {
          console.warn('Failed to log app usage:', logError);
          // We don't block execution if logging fails, but it's worth noting
        }
      }

      // Anonymous users must pass Turnstile before miniapp-auth will issue a run token.
      // Authenticated users are allowed through without it, so skip the widget to avoid
      // surfacing timeout noise for runs that would succeed anyway.
      let turnstileToken: string | null = null;
      if (!user && getTurnstileToken) {
        try {
          turnstileToken = await getTurnstileToken();
        } catch (err) {
          console.warn('Failed to get Turnstile token:', err);
          // Continue without token - server will decide if it's required
        }
      }

      // Step 2: Get JWT from miniapp-auth (also returns tier-based limits and credit info)
      const authHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (accessToken) {
        authHeaders['Authorization'] = `Bearer ${accessToken}`;
      }

      const authResponse = await fetch(`${supabaseUrl}/functions/v1/miniapp-auth`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ projectId, turnstileToken }),
      });

      if (!authResponse.ok) {
        const errorData = await authResponse.json();
        const error = new Error(errorData.error || 'Failed to authenticate') as Error & { code?: string };
        error.code = errorData.code;
        throw error;
      }

      const { token, serviceUrl, limits } = await authResponse.json();

      // Use tier-based payload limit from server (fallback to 10 MB)
      const maxPayloadBytes = limits?.maxPayloadBytes || (10 * 1024 * 1024);
      const maxPayloadMB = Math.round(maxPayloadBytes / (1024 * 1024));

      // Check combined input size against tier-based limit
      let totalSize = 0;
      for (const entry of entries) {
        if (entry.type === 'file') {
          totalSize += (entry.data as number[]).length;
        } else {
          totalSize += new TextEncoder().encode(entry.data as string).length;
        }
      }

      if (totalSize > maxPayloadBytes) {
        const sizeMB = (totalSize / (1024 * 1024)).toFixed(1);
        const errorMsg = `Combined input size (${sizeMB} MB) exceeds the ${maxPayloadMB} MB limit for your plan. Please reduce the size of your input files or upgrade your plan.`;
        setError(errorMsg);
        setStreaming({ status: 'failed', outputFiles: [], error: errorMsg });
        const errorFn = (window as any).__llambadaError;
        if (errorFn) errorFn({ code: 'INPUT_TOO_LARGE', message: errorMsg });
        return { error: 'Input size limit exceeded' };
      }

      // Step 3: Call Cloud Run Service directly with SSE
      setStreaming((prev) => ({ ...prev, status: 'running' }));

      // Notify iframe that job is starting - this triggers OPFS clearing
      // to prevent stale files from previous job runs
      const startedFn = (window as any).__llambadaStarted;
      if (startedFn) startedFn();

      // Reconstruct FormData from entries
      const formData = new FormData();
      for (const entry of entries) {
        if (entry.type === 'file') {
          const uint8Array = new Uint8Array(entry.data as number[]);
          const blob = new Blob([uint8Array], { type: entry.contentType });
          const file = new File([blob], entry.filename || entry.key, { type: entry.contentType });
          formData.append(entry.key, file);
        } else {
          formData.append(entry.key, entry.data as string);
        }
      }

      let response: Response;
      try {
        response = await fetch(`${serviceUrl}/run`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });
      } catch (fetchErr) {
        throw fetchErr;
      }

      if (!response.ok) {
        // Try to read error message from response
        let errorMessage = 'Failed to connect to mini-app service';
        try {
          const text = await response.text();
          // Try to parse SSE error event
          const match = text.match(/data:\s*(\{.*\})/);
          if (match) {
            const data = JSON.parse(match[1]);
            if (data.message) errorMessage = data.message;
          } else if (text) {
            errorMessage = text.slice(0, 200);
          }
        } catch (e) {
          // ignore parse errors
        }
        throw new Error(errorMessage);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      // Step 4: Handle SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventType = '';
      const outputFiles: Array<{ filename: string; size: number; content?: string }> = [];

      // Track application-level errors detected in logs
      let detectedAppError = false;
      let appErrorMessage = '';

      // Track SSE-level errors (from 'error' events)
      let sseError: string | null = null;

      // Track whether we received a terminal SSE event (complete or error)
      let receivedTerminalEvent = false;

      while (true) {
        const readResult = await reader.read();
        const { done, value } = readResult;
        if (done) {
          console.log('[Llambada] Stream done, remaining buffer length:', buffer.length);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7);
            console.log('[Llambada] SSE event type:', eventType);
          } else if (line.startsWith('data: ') && eventType) {
            let data;
            try {
              data = JSON.parse(line.slice(6));
            } catch (parseError) {
              console.error('[Llambada] Failed to parse SSE data for event:', eventType, 'line length:', line.length, parseError);
              eventType = '';
              continue;
            }

            switch (eventType) {
              case 'progress':
                setStreaming((prev) => ({
                  ...prev,
                  progress: { percent: data.percent, message: data.message },
                }));
                // Send progress to iframe
                const progressSyncFn = (window as any).__llambadaSyncProgress;
                if (progressSyncFn) {
                  progressSyncFn(data);
                }
                // Also sync progress.json to OPFS so frontend can read it
                const syncProgressFile = (window as any).__llambadaSyncFile;
                if (syncProgressFile) {
                  try {
                    const progressJson = JSON.stringify(data);
                    const progressData = new TextEncoder().encode(progressJson);
                    await syncProgressFile('progress.json', progressData.buffer);
                  } catch {
                    // Ignore sync errors for progress
                  }
                }
                break;

              case 'output':
                outputFiles.push({ filename: data.filename, size: data.size });
                break;

              case 'file':
                // Sync file to iframe's OPFS
                console.log('[Llambada] Received file event:', data.filename, 'hasContent:', !!data.content, 'contentLength:', data.content?.length || 0);
                if (data.content) {
                  const syncFn = (window as any).__llambadaSyncFile;
                  if (syncFn) {
                    try {
                      // Use chunked base64 decoding for large files (atob fails on very large strings)
                      const base64 = data.content;
                      const binaryString = atob(base64);
                      const len = binaryString.length;
                      const bytes = new Uint8Array(len);
                      for (let i = 0; i < len; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                      }
                      await syncFn(data.filename, bytes.buffer);
                    } catch (syncError) {
                      console.error('[Llambada] Failed to sync file:', data.filename, syncError);
                    }
                  }
                  const fileEntry = outputFiles.find((f) => f.filename === data.filename);
                  if (fileEntry) {
                    fileEntry.content = data.content;
                  }
                } else if (data.error) {
                  console.error('[Llambada] File error from backend:', data.filename, data.error);
                  setError(`Output file "${data.filename}" could not be delivered: ${data.error}`);
                }
                break;

              case 'logs': {
                // Store execution logs
                // Detect application-level errors even if exit code is 0
                // Only treat structured exceptions/tracebacks as app-level failures.
                // Tooling warnings (for example Chromium "[...:ERROR:...]") are noisy
                // but non-fatal and must not override a successful exit.
                const stderr = data.stderr || '';
                const tracebackMarker = /Traceback \(most recent call last\):/i.test(stderr);
                const pythonExceptionLine =
                  /(?:^|\n)(?:RuntimeError|ValueError|TypeError|NameError|ImportError|FileNotFoundError|AssertionError|Exception):/i.test(stderr);
                const hasAppError =
                  stderr.includes('[EXCEPTION]') ||
                  tracebackMarker ||
                  pythonExceptionLine;

                // Update local tracking variables for use in 'complete' handler
                if (hasAppError) {
                  detectedAppError = true;
                  // Extract error message from stderr for display
                  const errorMatch = stderr.match(/\[EXCEPTION\]\s*(.+?)(?:\n|$)/);
                  appErrorMessage = errorMatch
                    ? errorMatch[1]
                    : 'Application error (check logs for details)';
                }

                setStreaming((prev) => ({
                  ...prev,
                  logs: {
                    stdout: data.stdout || '',
                    stderr: stderr,
                    exitCode: data.exitCode ?? -1,
                    hasAppError, // Track if app-level error was detected
                  },
                }));
                break;
              }

              case 'log':
                // Real-time log streaming
                setStreaming((prev) => {
                  const currentLogs = prev.streamingLogs || { stdout: '', stderr: '' };
                  if (data.stream === 'stdout') {
                    return {
                      ...prev,
                      streamingLogs: {
                        ...currentLogs,
                        stdout: currentLogs.stdout + data.line + '\n',
                      },
                    };
                  } else if (data.stream === 'stderr') {
                    return {
                      ...prev,
                      streamingLogs: {
                        ...currentLogs,
                        stderr: currentLogs.stderr + data.line + '\n',
                      },
                    };
                  }
                  return prev;
                });
                break;

              case 'complete': {
                receivedTerminalEvent = true;
                console.log('[Llambada] Complete event received, outputFiles:', outputFiles.map(f => f.filename));

                if (detectedAppError) {
                  // Application-level error was detected in logs
                  setStreaming((prev) => ({
                    ...prev,
                    status: 'failed',
                    error: appErrorMessage,
                    outputFiles,
                  }));
                  // Notify iframe of error
                  const errorFn = (window as any).__llambadaError;
                  if (errorFn) {
                    errorFn({ code: 'EXECUTION_FAILED', message: appErrorMessage });
                  }
                } else {
                  // Normal completion
                  setStreaming((prev) => ({
                    ...prev,
                    status: 'completed',
                    outputFiles,
                  }));
                  // Notify iframe of completion
                  const completeFn = (window as any).__llambadaComplete;
                  if (completeFn) {
                    completeFn(outputFiles);
                  }
                }
                break;
              }

              case 'error':
                receivedTerminalEvent = true;
                sseError = data.message; // Track the error for return value
                setStreaming((prev) => ({
                  ...prev,
                  status: 'failed',
                  error: data.message,
                }));
                // Notify iframe of error with structured code
                const sseErrorFn = (window as any).__llambadaError;
                if (sseErrorFn) {
                  sseErrorFn({
                    code: data.code || 'EXECUTION_FAILED',
                    message: data.message,
                    details: data,
                  });
                }
                break;
            }
            eventType = '';
          }
        }
      }

      // Stream ended without a 'complete' or 'error' event — the backend
      // process was killed externally (e.g. Cloud Run OOM).
      if (!receivedTerminalEvent && !sseError) {
        const errorMsg = 'Backend process was terminated unexpectedly (possibly out of memory).';
        setStreaming((prev) => ({
          ...prev,
          status: 'failed',
          error: errorMsg,
          streamingLogs: {
            stdout: prev.streamingLogs?.stdout || '',
            stderr: (prev.streamingLogs?.stderr || '') + errorMsg + '\n',
          },
          logs: {
            stdout: prev.streamingLogs?.stdout || '',
            stderr: (prev.streamingLogs?.stderr || '') + errorMsg + '\n',
            exitCode: -1,
            hasAppError: true,
          },
        }));
        const errorFn = (window as any).__llambadaError;
        if (errorFn) {
          errorFn({ code: 'STREAM_TERMINATED', message: errorMsg });
        }
        return { error: errorMsg };
      }

      // Return error if one was received via SSE
      if (sseError) {
        return { error: sseError };
      }

      // Also check for application-level errors detected in logs
      if (detectedAppError) {
        return { error: appErrorMessage };
      }

      // Successful runs may consume credits; refresh both auth and anonymous
      // credit hooks with short retries so the slot counter animates as soon
      // as backend deduction becomes visible.
      refreshCreditDisplays();

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to run mini-app';
      const errorCode = (err as Error & { code?: string })?.code;
      setError(errorMessage);
      setStreaming((prev) => ({ ...prev, status: 'failed', error: errorMessage, errorCode }));
      // Notify iframe of error
      const catchErrorFn = (window as any).__llambadaError;
      if (catchErrorFn) {
        catchErrorFn({
          code: errorCode || 'EXECUTION_FAILED',
          message: errorMessage,
        });
      }
      return { error: errorMessage, errorCode };
    }

    }; // end runWithLock

    // Use Web Locks API (if available) to prevent the browser from freezing
    // or discarding this tab while the SSE stream is active.
    if (navigator.locks) {
      return navigator.locks.request(
        `llambada-miniapp-run-${projectId}`,
        { mode: 'exclusive', ifAvailable: true },
        async (_lock) => {
          return runWithLock();
        }
      );
    }
    return runWithLock();
  }, [projectId, accessToken, user, getTurnstileToken, refreshCreditDisplays]);

  // Reset streaming state (useful after Claude Code updates the code)
  const resetStreaming = useCallback(() => {
    setStreaming({
      status: 'idle',
      outputFiles: [],
    });
    setError(null);
  }, []);

  return { streaming, error, runMiniApp, resetStreaming };
}

export function useProject(projectId: string | undefined, getTurnstileToken?: () => Promise<string | null>) {
  const { session, authReady } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use shared mini-app runner with optional Turnstile token
  const { streaming, runMiniApp, resetStreaming } = useMiniAppRunner(projectId, session?.access_token, getTurnstileToken);

  const fetchProject = useCallback(async () => {
    if (!projectId) {
      setProject(null);
      setLoading(false);
      return;
    }

    // Wait for auth to be ready before fetching
    if (!authReady || !session) {
      // Keep loading state true while waiting for auth
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await dbSelect<Project>('projects', {
        token: session.access_token,
        filters: { id: `eq.${projectId}` },
        single: true,
      });

      if (fetchError) throw fetchError;
      setProject(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch project');
    } finally {
      setLoading(false);
    }
  }, [projectId, session, authReady]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const updateProject = async (updates: {
    name?: string;
    description?: string;
    specification?: string | null;
    logo_emoji?: string | null;
    type?: MiniAppType | null;
    is_published?: boolean;
    required_tier?: string | null;
    monetization_mode?: string;
    requires_auth?: boolean;
  }) => {
    if (!projectId || !session) return { error: 'No project ID or not authenticated' };

    // If this is a family fork and name or description is being changed,
    // mark it as customized so "Create / Recreate Selected Forks" won't overwrite it.
    const isFamilyFork = !!project?.forked_from && !!project?.family_params;
    const isMetadataEdit = ('name' in updates) || ('description' in updates);
    const payload = (isFamilyFork && isMetadataEdit && !project?.is_family_customized)
      ? { ...updates, is_family_customized: true }
      : updates;

    const { data, error: updateError } = await dbUpdate<Project>(
      'projects',
      payload,
      { id: `eq.${projectId}` },
      { token: session.access_token }
    );

    if (updateError) return { error: updateError.message };

    if (updates.is_published === true) {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const syncResponse = await fetch(
          `${supabaseUrl}/functions/v1/project-code?projectId=${projectId}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'sync-backend-infra-on-publish' }),
          }
        );
        if (!syncResponse.ok) {
          const syncError = await syncResponse.json().catch(() => null);
          return { error: syncError?.error || 'Published, but failed to sync backend infrastructure' };
        }
      } catch (err) {
        return { error: err instanceof Error ? err.message : 'Published, but failed to sync backend infrastructure' };
      }
    }

    if (data) setProject(data);
    return { data };
  };

  return {
    project,
    loading,
    error,
    streaming,
    updateProject,
    runMiniApp,
    resetStreaming,
    refetch: fetchProject,
  };
}

interface UseConversationsOptions {
  enabled?: boolean;
}

export function useConversations(projectId: string | undefined, options?: UseConversationsOptions) {
  const { session, authReady } = useAuth();
  const enabled = options?.enabled ?? true;
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConversations = useCallback(async () => {
    if (!projectId || !enabled) {
      setConversations([]);
      setLoading(false);
      return;
    }

    // Wait for auth to be ready before fetching
    if (!authReady || !session) {
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await dbSelect<Conversation[]>('conversations', {
        token: session.access_token,
        filters: { project_id: `eq.${projectId}` },
        order: 'updated_at.desc',
      });

      if (fetchError) throw fetchError;
      setConversations(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  }, [projectId, session, authReady, enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchConversations();
  }, [fetchConversations, enabled]);

  const createConversation = async (title?: string) => {
    if (!projectId || !session) return { error: 'No project ID or not authenticated' };

    const { data, error: insertError } = await dbInsert<Conversation>('conversations',
      { project_id: projectId, title: title || 'New Conversation' },
      { token: session.access_token }
    );

    if (insertError || !data) return { error: insertError?.message || 'Failed to create conversation' };

    setConversations((prev) => [data, ...prev]);
    return { data };
  };

  const updateConversation = async (id: string, title: string) => {
    if (!session) return { error: 'Not authenticated' };

    const { data, error: updateError } = await dbUpdate<Conversation>(
      'conversations',
      { title },
      { id: `eq.${id}` },
      { token: session.access_token }
    );

    if (updateError) return { error: updateError.message };

    if (data) {
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? data : c))
      );
    }
    return { data };
  };

  const deleteConversation = async (id: string) => {
    if (!session) return { error: 'Not authenticated' };

    const { error: deleteError } = await dbDelete('conversations',
      { id: `eq.${id}` },
      { token: session.access_token }
    );

    if (deleteError) return { error: deleteError.message };

    setConversations((prev) => prev.filter((c) => c.id !== id));
    return { success: true };
  };

  return {
    conversations,
    loading,
    error,
    fetchConversations,
    createConversation,
    updateConversation,
    deleteConversation,
  };
}

export function useMessages(conversationId: string | undefined, projectId?: string) {
  const { session, isAdmin, ensureFreshToken, subscriptionTier } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [streamingStatus, setStreamingStatus] = useState<string>('');
  const [lastModifiedFiles, setLastModifiedFiles] = useState<string[]>([]);
  const [buildStarted, setBuildStarted] = useState(false);
  const [modifiedSiblingProjectIds, setModifiedSiblingProjectIds] = useState<string[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const cancelRequestedRef = useRef(false);
  const sendingRef = useRef(false);
  const attachmentPayloadByMessageIdRef = useRef<Map<string, AttachedFilePayload[]>>(new Map());

  const filterAssistantMessageForDisplay = useCallback((content: string): string => {
    if (isAdmin) return content;
    if (!shouldFilterKimiOutput(content)) return content;
    return filterKimiOutput(content, isAdmin, true);
  }, [isAdmin]);

  const buildAttachmentStoragePath = useCallback((messageId: string, index: number, originalName: string) => {
    const safeName = sanitizeAttachmentFilename(originalName);
    const prefixedName = `${String(index + 1).padStart(2, '0')}_${safeName}`;
    return `${projectId}/sessions/${conversationId}/attachments/${messageId}/${prefixedName}`;
  }, [conversationId, projectId]);

  const persistMessageAttachments = useCallback(async (
    messageId: string,
    attachments: AttachedFilePayload[],
    token: string,
  ): Promise<void> => {
    if (!messageId || attachments.length === 0 || !projectId || !conversationId) return;

    const uploadedPaths: string[] = [];
    const rowsToInsert: MessageAttachmentInsert[] = [];

    try {
      for (let index = 0; index < attachments.length; index += 1) {
        const attachment = attachments[index];
        const storagePath = buildAttachmentStoragePath(messageId, index, attachment.name);
        const blob = base64ToBlob(attachment.contentBase64, attachment.mimeType);
        const { error: uploadError } = await uploadFile(
          'conversation-files',
          storagePath,
          blob,
          { token },
        );
        if (uploadError) {
          throw uploadError;
        }
        uploadedPaths.push(storagePath);
        rowsToInsert.push({
          message_id: messageId,
          file_name: attachment.name,
          mime_type: attachment.mimeType || 'application/octet-stream',
          file_size: attachment.size,
          storage_path: storagePath,
        });
      }

      const { error: insertError } = await dbInsert<MessageAttachmentRecord[]>(
        'message_attachments',
        rowsToInsert,
        { token, returning: false },
      );
      if (insertError) {
        throw insertError;
      }
    } catch (err) {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      await Promise.all(
        uploadedPaths.map(async (path) => {
          try {
            await fetch(`${supabaseUrl}/storage/v1/object/conversation-files/${path}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`,
                'apikey': supabaseAnonKey,
              },
            });
          } catch {
            // Ignore cleanup failures; this path is best-effort.
          }
        }),
      );
      throw err;
    }
  }, [buildAttachmentStoragePath, conversationId, projectId]);

  const fetchStoredAttachmentsForMessage = useCallback(async (
    messageId: string,
  ): Promise<AttachedFilePayload[] | null> => {
    if (!messageId || !session?.access_token) return null;

    const { data, error: attachmentsError } = await dbSelect<MessageAttachmentRecord[]>(
      'message_attachments',
      {
        token: session.access_token,
        filters: { message_id: `eq.${messageId}` },
        order: 'created_at.asc',
      },
    );
    if (attachmentsError || !data || data.length === 0) {
      return null;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const payload: AttachedFilePayload[] = [];

    for (const row of data) {
      const response = await fetch(
        `${supabaseUrl}/storage/v1/object/conversation-files/${row.storage_path}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': supabaseAnonKey,
          },
        },
      );
      if (!response.ok) {
        continue;
      }
      const blob = await response.blob();
      const contentBase64 = await blobToBase64(blob, row.file_name);
      payload.push({
        name: row.file_name,
        mimeType: row.mime_type || blob.type || 'application/octet-stream',
        size: row.file_size || blob.size,
        contentBase64,
      });
    }

    return payload.length > 0 ? payload : null;
  }, [session?.access_token]);

  const fetchAttachmentRowsForMessages = useCallback(async (
    messageIds: string[],
  ): Promise<MessageAttachmentRecord[]> => {
    if (messageIds.length === 0 || !session?.access_token) return [];

    const idsFilter = messageIds.join(',');
    const { data, error: attachmentsError } = await dbSelect<MessageAttachmentRecord[]>(
      'message_attachments',
      {
        token: session.access_token,
        filters: { message_id: `in.(${idsFilter})` },
      },
    );
    if (attachmentsError || !data) return [];
    return data;
  }, [session?.access_token]);

  const deleteStoredAttachmentObjects = useCallback(async (
    attachmentRows: MessageAttachmentRecord[],
  ): Promise<void> => {
    if (attachmentRows.length === 0 || !session?.access_token) return;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    await Promise.all(
      attachmentRows.map(async (row) => {
        try {
          await fetch(`${supabaseUrl}/storage/v1/object/conversation-files/${row.storage_path}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': supabaseAnonKey,
            },
          });
        } catch {
          // Ignore storage deletion failures in cleanup path.
        }
      }),
    );
  }, [session?.access_token]);

  const fetchTranscriptMessages = useCallback(async (): Promise<Message[] | null> => {
    if (!conversationId || !projectId || !session?.access_token) return null;

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(
        `${supabaseUrl}/storage/v1/object/conversation-files/${projectId}/${conversationId}/transcript.json`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': supabaseAnonKey,
          },
        }
      );
      if (!response.ok) return null;

      const transcript = await response.json() as { messages?: Array<{ role?: string; content?: string; timestamp?: string }> };
      if (!Array.isArray(transcript.messages) || transcript.messages.length === 0) return null;

      const fallbackMessages: Message[] = transcript.messages.map((msg, index) => {
        const role = msg.role === 'user' || msg.role === 'assistant' || msg.role === 'system'
          ? msg.role
          : 'assistant';
        const content = typeof msg.content === 'string' ? msg.content : '';
        const createdAt = typeof msg.timestamp === 'string' && msg.timestamp ? msg.timestamp : new Date().toISOString();
        return {
          id: `transcript-${conversationId}-${index}`,
          conversation_id: conversationId,
          role,
          content: role === 'assistant' ? filterAssistantMessageForDisplay(content) : content,
          created_at: createdAt,
        };
      });

      return fallbackMessages;
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, projectId, session?.access_token, filterAssistantMessageForDisplay]);

  const fetchPersistedStreamSnapshot = useCallback(async (): Promise<PersistedAgentStreamSnapshot | null> => {
    if (!conversationId || !projectId || !session?.access_token) return null;

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(
        `${supabaseUrl}/storage/v1/object/conversation-files/${projectId}/${conversationId}/stream.json`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': supabaseAnonKey,
          },
          cache: 'no-store',
        }
      );

      if (!response.ok) return null;

      const snapshot = await response.json() as {
        status?: unknown;
        content?: unknown;
        streaming_status?: unknown;
        streamingStatus?: unknown;
        error_message?: unknown;
        errorMessage?: unknown;
        updated_at?: unknown;
        updatedAt?: unknown;
      };

      const rawContent = typeof snapshot.content === 'string' ? snapshot.content : '';
      return {
        status: typeof snapshot.status === 'string' ? snapshot.status : '',
        content: rawContent ? filterAssistantMessageForDisplay(rawContent) : '',
        streamingStatus: typeof snapshot.streaming_status === 'string'
          ? snapshot.streaming_status
          : (typeof snapshot.streamingStatus === 'string' ? snapshot.streamingStatus : ''),
        errorMessage: typeof snapshot.error_message === 'string'
          ? snapshot.error_message
          : (typeof snapshot.errorMessage === 'string' ? snapshot.errorMessage : null),
        updatedAt: typeof snapshot.updated_at === 'string'
          ? snapshot.updated_at
          : (typeof snapshot.updatedAt === 'string' ? snapshot.updatedAt : null),
      };
    } catch {
      return null;
    }
  }, [conversationId, projectId, session?.access_token, filterAssistantMessageForDisplay]);

  const refetchMessagesFromDb = useCallback(async (): Promise<Message[] | null> => {
    if (!conversationId) return null;

    const { data: updatedMessages, error: fetchError } = await dbSelect<Message[]>('messages', {
      token: session?.access_token,
      filters: { conversation_id: `eq.${conversationId}` },
      order: 'created_at.asc',
    });

    if (!fetchError && updatedMessages) {
      const filteredMessages = updatedMessages.map(msg => {
        if (msg.role === 'assistant') {
          return { ...msg, content: filterAssistantMessageForDisplay(msg.content) };
        }
        return msg;
      });
      setMessages(filteredMessages);
      return filteredMessages;
    }
    return null;
  }, [conversationId, session?.access_token, filterAssistantMessageForDisplay]);

  // Reset sending state when switching conversations so the fetch below isn't
  // blocked by a sendingRef left over from the previous conversation's
  // reconcile-pending-run polling.
  const prevConversationIdRef = useRef<string | undefined>(conversationId);
  useEffect(() => {
    if (prevConversationIdRef.current !== conversationId) {
      const wasActivelyStreaming = sendingRef.current && abortControllerRef.current !== null;
      prevConversationIdRef.current = conversationId;
      if (sendingRef.current && !wasActivelyStreaming) {
        sendingRef.current = false;
        setSending(false);
        setStreamingContent('');
        setStreamingStatus('');
      }
    }
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    const fetchMessages = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await dbSelect<Message[]>('messages', {
          token: session?.access_token,
          filters: { conversation_id: `eq.${conversationId}` },
          order: 'created_at.asc',
        });

        if (fetchError) throw fetchError;

        // If a sendMessage call is in progress, skip overwriting messages state.
        // sendMessage manages optimistic messages during its lifecycle and calls
        // refetchMessagesFromDb when finished, so this async fetch (which may
        // have started before the DB insert) would discard the optimistic user
        // message that sendMessage already added.
        if (sendingRef.current) {
          setLoading(false);
          return;
        }

        // Filter assistant messages for non-admin users
        let filteredMessages = (data || []).map(msg => {
          if (msg.role === 'assistant') {
            return { ...msg, content: filterAssistantMessageForDisplay(msg.content) };
          }
          return msg;
        });

        const hasUserMessages = filteredMessages.some(msg => msg.role === 'user');
        if (!hasUserMessages) {
          const transcriptMessages = await fetchTranscriptMessages();
          if (transcriptMessages?.some(msg => msg.role === 'user')) {
            filteredMessages = transcriptMessages;
          }
        }

        // A send can begin while we await transcript fallback above.
        // Re-check before mutating state so we don't clobber optimistic user messages.
        if (sendingRef.current) {
          return;
        }

        setMessages(filteredMessages);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch messages');
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  // NOTE: Depend on session?.access_token (not the full session object) so that
  // this effect only re-runs when the token string actually changes.  Previously
  // the dependency on the full `session` object caused the effect to fire on every
  // token refresh (which creates a new object reference), calling setLoading(true)
  // and briefly hiding all messages behind a spinner — even while sendMessage was
  // actively streaming.  The access_token string is the only value this effect uses
  // from the session, so narrowing the dependency is both correct and avoids the
  // flickering/hiding bug.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, session?.access_token, fetchTranscriptMessages, filterAssistantMessageForDisplay]);

  useEffect(() => {
    if (!conversationId || !session) return;

    let disposed = false;

    const reconcilePendingRun = async () => {
      const pendingRun = readPendingAgentRun(conversationId);
      if (!pendingRun || disposed) return;
      const activelyStreamingInThisTab = sendingRef.current && abortControllerRef.current !== null;
      if (activelyStreamingInThisTab) return;
      const startedAtMs = Date.parse(pendingRun.startedAt);
      if (!Number.isNaN(startedAtMs) && Date.now() - startedAtMs > 30 * 60 * 1000) {
        clearPendingAgentRun(conversationId);
        sendingRef.current = false;
        setSending(false);
        setStreamingStatus('');
        setStreamingContent('');
        setError('Background run timed out. Please retry your request.');
        return;
      }

      if (!sendingRef.current) {
        sendingRef.current = true;
        setSending(true);
      }
      setError(null);
      setStreamingStatus(BACKGROUND_RECOVERY_STATUS);

      const locallyRecoveredContent = pendingRun.partialAssistantContent || '';
      if (locallyRecoveredContent) {
        setStreamingContent((prev) => (
          locallyRecoveredContent.length > prev.length
            ? locallyRecoveredContent
            : prev
        ));
      }

      const persistedSnapshot = await fetchPersistedStreamSnapshot();
      if (disposed) return;

      if (persistedSnapshot?.content) {
        setStreamingContent((prev) => (
          persistedSnapshot.content.length > prev.length
            ? persistedSnapshot.content
            : prev
        ));
      }

      if (persistedSnapshot?.content || persistedSnapshot?.streamingStatus) {
        upsertPendingAgentRun(conversationId, {
          partialAssistantContent: persistedSnapshot.content || pendingRun.partialAssistantContent,
          streamingStatus: persistedSnapshot.streamingStatus || pendingRun.streamingStatus,
          streamUpdatedAt: persistedSnapshot.updatedAt || new Date().toISOString(),
        });
      }

      const refreshedMessages = await refetchMessagesFromDb();
      if (!refreshedMessages || disposed) return;

      if (hasAssistantAfterUserMessage(refreshedMessages, pendingRun.userMessageId)) {
        clearPendingAgentRun(conversationId);
        sendingRef.current = false;
        setSending(false);
        setStreamingStatus('');
        setStreamingContent('');
        cancelRequestedRef.current = false;
        return;
      }

      if (persistedSnapshot?.status === 'error') {
        clearPendingAgentRun(conversationId);
        sendingRef.current = false;
        setSending(false);
        setStreamingStatus('');
        setError(persistedSnapshot.errorMessage || 'Background run failed. Please retry your request.');
        cancelRequestedRef.current = false;
      }
    };

    void reconcilePendingRun();
    const pollId = window.setInterval(() => {
      void reconcilePendingRun();
    }, 4000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void reconcilePendingRun();
      }
    };
    const handleFocus = () => {
      void reconcilePendingRun();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      disposed = true;
      window.clearInterval(pollId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [conversationId, session, refetchMessagesFromDb, fetchPersistedStreamSnapshot]);

  const sendMessage = async (
    content: string,
    model?: string,
    attachments?: File[],
    preSerializedAttachments?: AttachedFilePayload[]
  ) => {
    if (!conversationId || !session) return { error: 'Not ready' };

    const selectedModel = model || 'claude-opus-4-6';
    const assistantCountBeforeRun = messages.filter(msg => msg.role === 'assistant').length;
    let filteredContent = '';
    let streamErrorMessage: string | null = null;
    let sawCompleteEvent = false;
    let persistedUserMessageId: string | null = null;
    let userAccessToken = session.access_token;
    let pendingRunStartedAt = new Date().toISOString();
    let lastPendingProgressWriteMs = 0;
    let latestStreamingStatus = '';

    const persistPendingRunProgress = (
      updates: { partialAssistantContent?: string; streamingStatus?: string },
      options?: { force?: boolean },
    ) => {
      if (!conversationId || !persistedUserMessageId) return;
      const now = Date.now();
      if (!options?.force && now - lastPendingProgressWriteMs < 300) {
        return;
      }
      lastPendingProgressWriteMs = now;
      upsertPendingAgentRun(conversationId, {
        userMessageId: persistedUserMessageId,
        startedAt: pendingRunStartedAt,
        partialAssistantContent: updates.partialAssistantContent ?? filteredContent,
        streamingStatus: updates.streamingStatus ?? latestStreamingStatus,
        streamUpdatedAt: new Date(now).toISOString(),
      });
    };

    const setStreamingStatusWithRecovery = (
      status: string,
      options?: { forcePendingWrite?: boolean },
    ) => {
      latestStreamingStatus = status;
      setStreamingStatus(status);
      persistPendingRunProgress(
        { partialAssistantContent: filteredContent, streamingStatus: status },
        { force: options?.forcePendingWrite },
      );
    };

    const finalizeSend = (
      result: { data?: boolean; error?: string },
      options?: { clearPendingRun?: boolean; keepSending?: boolean; keepStreamingStatus?: boolean; keepStreamingContent?: boolean },
    ) => {
      if (options?.clearPendingRun !== false) {
        clearPendingAgentRun(conversationId);
      }
      if (!options?.keepStreamingContent) {
        setStreamingContent('');
      }
      if (!options?.keepStreamingStatus) {
        setStreamingStatus('');
      }
      sendingRef.current = !!options?.keepSending;
      setSending(!!options?.keepSending);
      cancelRequestedRef.current = false;
      abortControllerRef.current = null;
      return result;
    };

    const persistAssistantMessage = async (assistantContent: string) => {
      const trimmed = assistantContent.trim();
      if (!trimmed) return;
      const { error: insertError } = await dbInsert<Message>('messages',
        {
          conversation_id: conversationId,
          role: 'assistant',
          content: trimmed,
        },
        { token: session.access_token }
      );
      if (insertError) {
        console.error('Failed to persist assistant message:', insertError);
      }
    };

    const handoffToBackground = async () => {
      setError(null);
      setStreamingStatusWithRecovery(BACKGROUND_RECOVERY_STATUS, { forcePendingWrite: true });
      persistPendingRunProgress(
        {
          partialAssistantContent: filteredContent,
          streamingStatus: BACKGROUND_RECOVERY_STATUS,
        },
        { force: true },
      );
      await refetchMessagesFromDb();
      return finalizeSend(
        { data: true },
        { clearPendingRun: false, keepSending: true, keepStreamingStatus: true, keepStreamingContent: true },
      );
    };

    try {
      // Compute display content synchronously so the optimistic message appears
      // in the same render batch as setSending(true), before any async work.
      let finalContent = content;
      const attachmentNames = attachments && attachments.length > 0
        ? attachments.map((file) => file.name)
        : (preSerializedAttachments || []).map((file) => file.name);
      if (attachmentNames.length > 0) {
        const attachmentLabels = attachmentNames.map((name) => `[Attached: ${name}]`).join('\n');
        finalContent = `${content}\n\n${attachmentLabels}`;
      }

      // Optimistically add user message
      const tempUserMessage: Message = {
        id: `temp-${Date.now()}`,
        conversation_id: conversationId,
        role: 'user',
        content: finalContent,
        created_at: new Date().toISOString(),
      };

      sendingRef.current = true;
      setSending(true);
      setError(null);
      setStreamingContent('');
      setStreamingStatusWithRecovery('Saving message...');
      cancelRequestedRef.current = false;
      abortControllerRef.current = new AbortController();
      setMessages((prev) => [...prev, tempUserMessage]);

      let attachedFilesPayload: AttachedFilePayload[] = [];
      if (preSerializedAttachments && preSerializedAttachments.length > 0) {
        attachedFilesPayload = preSerializedAttachments;
      } else if (attachments && attachments.length > 0) {
        setStreamingStatusWithRecovery('Preparing attachments...');
        attachedFilesPayload = await serializeAttachedFiles(attachments, subscriptionTier);
      }

      // Persist user message immediately so it never depends on coder run success.
      // If the first insert fails, force-refresh token once and retry.
      let persistedUserMessage: Message | null = null;
      let userMessageError: Error | null = null;
      ({ data: persistedUserMessage, error: userMessageError } = await dbInsert<Message>('messages',
        {
          conversation_id: conversationId,
          role: 'user',
          content: finalContent,
        },
        { token: userAccessToken }
      ));

      if (userMessageError || !persistedUserMessage) {
        const refreshedToken = await ensureFreshToken({ forceRefresh: true });
        if (refreshedToken) {
          userAccessToken = refreshedToken;
          ({ data: persistedUserMessage, error: userMessageError } = await dbInsert<Message>('messages',
            {
              conversation_id: conversationId,
              role: 'user',
              content: finalContent,
            },
            { token: userAccessToken }
          ));
        }
      }

      if (userMessageError || !persistedUserMessage) {
        console.error('Failed to persist user message before run:', userMessageError);
        setMessages((prev) => prev.filter((msg) => msg.id !== tempUserMessage.id));
        setError('Failed to save your message. Please try again.');
        return finalizeSend({ error: 'Failed to save your message' });
      }

      persistedUserMessageId = persistedUserMessage.id;
      if (attachedFilesPayload.length > 0) {
        attachmentPayloadByMessageIdRef.current.set(persistedUserMessageId, attachedFilesPayload);
        try {
          await persistMessageAttachments(persistedUserMessageId, attachedFilesPayload, userAccessToken);
        } catch (attachmentPersistError) {
          console.warn('Failed to persist message attachments for resubmit:', attachmentPersistError);
        }
      }
      setMessages((prev) => {
        const tempIndex = prev.findIndex((msg) => msg.id === tempUserMessage.id);
        if (tempIndex === -1) {
          // If a parallel refresh replaced local state, still surface the saved user message immediately.
          return [...prev, persistedUserMessage];
        }
        return prev.map((msg) => (
          msg.id === tempUserMessage.id ? persistedUserMessage : msg
        ));
      });

      pendingRunStartedAt = new Date().toISOString();
      writePendingAgentRun({
        conversationId,
        userMessageId: persistedUserMessageId,
        startedAt: pendingRunStartedAt,
        partialAssistantContent: '',
        streamingStatus: latestStreamingStatus,
        streamUpdatedAt: pendingRunStartedAt,
      });

      setStreamingStatusWithRecovery('Authenticating...');

      // Step 1: Get JWT and service details from agentic-coder-auth
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const authResponse = await fetch(`${supabaseUrl}/functions/v1/agentic-coder-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userAccessToken}`,
        },
        body: JSON.stringify({
          conversationId,
          message: content,
          currentUserMessageId: persistedUserMessageId,
          model: selectedModel,
        }),
        signal: abortControllerRef.current?.signal,
      });

      if (!authResponse.ok) {
        const errorData = await authResponse.json();
        // Check if error is about needing API key
        if (errorData.needsApiKey) {
          const isKimi = selectedModel.startsWith('kimi-');
          const isGlm = selectedModel.startsWith('glm-');
          const isMinimax = selectedModel.startsWith('minimax-');
          const fallbackMsg = isGlm
            ? 'Please configure your GLM API key in Settings to use the coding AI'
            : isKimi
              ? 'Please configure your Kimi API key in Settings to use the coding AI'
              : isMinimax
                ? 'Please configure your MiniMax API key in Settings to use the coding AI'
                : 'Please configure your Anthropic API key in Settings to use Claude Code';
          throw new Error(errorData.error || fallbackMsg);
        }
        // Check if error is about usage limit
        if (errorData.usageLimitExceeded) {
          const limitError = new Error(errorData.error || 'AI usage limit exceeded');
          (limitError as any).usageLimitExceeded = true;
          (limitError as any).usage = errorData.usage;
          (limitError as any).limits = errorData.limits;
          throw limitError;
        }
        throw new Error(errorData.error || 'Failed to authenticate');
      }

      const {
        token,
        serviceUrl,
        kimiServiceUrl,
        glmServiceUrl,
        minimaxServiceUrl,
        projectId,
        isFirstMessage,
        isForkedProject,
        isBaseToolFork,
        baseToolForkMap,
        codeZipBase64,
        codeZipGcsPath,
        // SECURITY: No secrets are returned from the auth endpoint.
        // Cloud Run services read their own env vars for all API keys and service keys.
        canUseSponsoredAi: _canUseSponsoredAi,
        sponsoredAiCallsRemaining: _sponsoredAiCallsRemaining,
        projectDescription,
      } = await authResponse.json();

      // Step 2: Determine which service to call based on the selected model
      const isKimiModel = selectedModel.startsWith('kimi-');
      const isGlmModel = selectedModel.startsWith('glm-');
      const isMinimaxModel = selectedModel.startsWith('minimax-');
      const targetServiceUrl = isGlmModel
        ? (glmServiceUrl || serviceUrl)
        : isKimiModel
          ? (kimiServiceUrl || serviceUrl)
          : isMinimaxModel
            ? (minimaxServiceUrl || serviceUrl)
            : serviceUrl;

      // Prevent non-Claude models from silently falling back to the Claude service
      // (which would confusingly ask for an Anthropic API key)
      if ((isMinimaxModel && !minimaxServiceUrl) ||
          (isKimiModel && !kimiServiceUrl) ||
          (isGlmModel && !glmServiceUrl)) {
        const providerName = isMinimaxModel ? 'MiniMax' : isKimiModel ? 'Kimi' : 'GLM';
        throw new Error(`${providerName} service is not available. Please try again later or select a different model.`);
      }

      setStreamingStatusWithRecovery('Connecting to service...');

      const serviceResponse = await fetch(`${targetServiceUrl}/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: content,
          conversationId,
          currentUserMessageId: persistedUserMessageId,
          projectId,
          isFirstMessage,
          isForkedProject,
          isBaseToolFork: isBaseToolFork || false,
          baseToolForkMap: baseToolForkMap || null,
          codeZipBase64,
          codeZipGcsPath,
          attachedFiles: attachedFilesPayload.length > 0 ? attachedFilesPayload : undefined,
          // SECURITY: No secrets sent from browser. Cloud Run reads them from env vars + DB.
          model: selectedModel,
          projectDescription: projectDescription || undefined,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!serviceResponse.ok) {
        throw new Error('Failed to connect to service');
      }

      // Process SSE stream
      const reader = serviceResponse.body?.getReader();
      if (!reader) {
        throw new Error('No response stream');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      let currentEventType = '';
      const outputFilter = new KimiOutputFilterStream(isAdmin, isKimiModel || isGlmModel || isMinimaxModel);

      const processSseBlock = (rawBlock: string) => {
        const lines = rawBlock.split('\n');
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const dataStr = line.slice(6);
            try {
              const data = JSON.parse(dataStr);

              switch (currentEventType) {
                case 'status':
                  setStreamingStatusWithRecovery(data.message || '');
                  break;
                case 'text':
                  fullContent += data.content + '\n';
                  // Filter output for regular users viewing Kimi responses
                  const filteredChunk = outputFilter.process(data.content + '\n');
                  filteredContent += filteredChunk;
                  setStreamingContent(filteredContent);
                  persistPendingRunProgress({ partialAssistantContent: filteredContent });
                  break;
                case 'tool':
                  setStreamingStatusWithRecovery(`Using tool: ${data.name}`);
                  break;
                case 'error':
                  streamErrorMessage = data.message || 'An error occurred';
                  persistPendingRunProgress(
                    {
                      partialAssistantContent: filteredContent,
                      streamingStatus: latestStreamingStatus,
                    },
                    { force: true },
                  );
                  break;
                case 'build_started':
                  // Backend triggered a container rebuild - notify immediately
                  // so frontend can block the preview before the 'complete' event
                  setBuildStarted(true);
                  break;
                case 'sibling_forks_modified':
                  // Base-tool fork: sibling forks were modified
                  if (data.projectIds && Array.isArray(data.projectIds)) {
                    setModifiedSiblingProjectIds(data.projectIds);
                  }
                  break;
                case 'complete':
                  sawCompleteEvent = true;
                  // Message saved to DB - capture modified files
                  if (data.modifiedFiles && Array.isArray(data.modifiedFiles)) {
                    setLastModifiedFiles(data.modifiedFiles);
                  }
                  persistPendingRunProgress(
                    {
                      partialAssistantContent: filteredContent,
                      streamingStatus: latestStreamingStatus,
                    },
                    { force: true },
                  );
                  break;
              }
            } catch {
              // Ignore JSON parse errors for incomplete data
            }
          }
        }
        currentEventType = '';
      };

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE blocks from buffer.
        // Parsing by block avoids losing event/data pairing across chunk boundaries.
        const blocks = buffer.split('\n\n');
        buffer = blocks.pop() || ''; // Keep trailing partial block in buffer
        for (const block of blocks) {
          processSseBlock(block);
        }
      }

      // Process final trailing block if stream ended without delimiter.
      if (buffer.trim()) {
        processSseBlock(buffer);
      }

      // Stream completed - flush any remaining filtered content
      const remainingContent = outputFilter.flush();
      if (remainingContent) {
        filteredContent += remainingContent;
        setStreamingContent(filteredContent);
      }
      persistPendingRunProgress(
        {
          partialAssistantContent: filteredContent,
          streamingStatus: latestStreamingStatus,
        },
        { force: true },
      );

      if (!sawCompleteEvent) {
        const refreshedMessages = await refetchMessagesFromDb();
        const assistantCountAfterRun = (refreshedMessages || []).filter(msg => msg.role === 'assistant').length;
        const backendAlreadySavedAssistant = persistedUserMessageId
          ? hasAssistantAfterUserMessage(refreshedMessages || [], persistedUserMessageId)
          : assistantCountAfterRun > assistantCountBeforeRun;

        if (!backendAlreadySavedAssistant) {
          if (filteredContent.trim()) {
            await persistAssistantMessage(filteredContent);
          } else if (streamErrorMessage) {
            await persistAssistantMessage(streamErrorMessage);
          } else if (persistedUserMessageId) {
            return handoffToBackground();
          }
          await refetchMessagesFromDb();
        } else if (!streamErrorMessage) {
          return finalizeSend({ data: true });
        }
        return finalizeSend({ error: streamErrorMessage || 'Run did not complete' });
      }

      // Refetch messages from database with a small delay
      // to ensure the assistant message has been saved
      await new Promise(resolve => setTimeout(resolve, 500));
      await refetchMessagesFromDb();
      return finalizeSend({ data: true });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        if (cancelRequestedRef.current) {
          if (filteredContent.trim()) {
            await persistAssistantMessage(filteredContent);
            await refetchMessagesFromDb();
          }
          return finalizeSend({ error: 'Request cancelled' });
        }

        if (persistedUserMessageId) {
          return handoffToBackground();
        }

        return finalizeSend({ error: 'Request cancelled' });
      }

      // Check for usage limit exceeded error
      if (err instanceof Error && (err as any).usageLimitExceeded) {
        const usage = (err as any).usage;
        const limits = (err as any).limits;
        const isDailyLimit = usage && limits && usage.daily >= limits.daily;

        // Create a synthetic assistant message with the usage limit info
        const limitMessage = isDailyLimit
          ? `You've used all ${limits?.daily} AI calls for today. Your daily limit resets at midnight UTC.\n\nTo get more AI calls, you can upgrade your subscription: {{UPGRADE_LINK}}`
          : `You've reached your AI usage limit for this month.\n\nTo get more AI calls, you can upgrade your subscription: {{UPGRADE_LINK}}`;
        await persistAssistantMessage(limitMessage);
        await refetchMessagesFromDb();
        return finalizeSend({ error: 'Usage limit exceeded' });
      }

      // Check for API key configuration error
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      const isApiKeyError = errorMessage.toLowerCase().includes('api key') ||
        errorMessage.toLowerCase().includes('configure your anthropic') ||
        errorMessage.toLowerCase().includes('configure your kimi') ||
        errorMessage.toLowerCase().includes('configure your glm') ||
        errorMessage.toLowerCase().includes('configure your minimax');

      if (isApiKeyError) {
        // Create a synthetic assistant message with helpful information
        const isKimi = selectedModel.startsWith('kimi-');
        const isGlm = selectedModel.startsWith('glm-');
        const isMinimax = selectedModel.startsWith('minimax-');
        const apiKeyProvider = isMinimax ? 'MiniMax' : isGlm ? 'GLM' : isKimi ? 'Kimi' : 'Anthropic';
        const apiKeyMessage = `I need an API key to help you with coding tasks.\n\nPlease configure your ${apiKeyProvider} API key in Settings to use the coding AI.\n\nFor step-by-step instructions, check out our {{GUIDE_LINK}}.`;
        await persistAssistantMessage(apiKeyMessage);
        await refetchMessagesFromDb();
        return finalizeSend({ error: 'API key not configured' });
      }

      if (persistedUserMessageId && isTransientConnectionError(errorMessage)) {
        return handoffToBackground();
      }

      if (filteredContent.trim()) {
        await persistAssistantMessage(filteredContent);
        await refetchMessagesFromDb();
      } else if (streamErrorMessage) {
        await persistAssistantMessage(streamErrorMessage);
        await refetchMessagesFromDb();
      }

      setError(errorMessage);
      if (persistedUserMessageId) {
        await refetchMessagesFromDb();
      }
      return finalizeSend({ error: errorMessage });
    }
  };

  const clearLastModifiedFiles = () => setLastModifiedFiles([]);
  const clearBuildStarted = () => setBuildStarted(false);
  const clearModifiedSiblingProjectIds = () => setModifiedSiblingProjectIds([]);
  const stopLatestRun = useCallback(() => {
    if (!sendingRef.current) return false;
    cancelRequestedRef.current = true;
    setStreamingStatus('Stopping...');
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      return true;
    }
    if (conversationId) {
      clearPendingAgentRun(conversationId);
    }
    sendingRef.current = false;
    setSending(false);
    setStreamingStatus('');
    cancelRequestedRef.current = false;
    return true;
  }, [conversationId]);

  // Resubmit a user message: delete all messages from this one onwards and resend
  const resubmitMessage = async (messageId: string, model?: string) => {
    if (!conversationId || !session) return { error: 'Not ready' };
    if (messageId.startsWith('transcript-')) return { error: 'Cannot resubmit transcript-only messages' };

    // Find the message and its index
    const messageIndex = messages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) return { error: 'Message not found' };

    const messageToResubmit = messages[messageIndex];
    if (messageToResubmit.role !== 'user') return { error: 'Can only resubmit user messages' };
    let storedAttachmentPayload = attachmentPayloadByMessageIdRef.current.get(messageToResubmit.id);
    const { mainContent, attachmentNames } = splitTrailingAttachmentLabels(messageToResubmit.content);
    const hasAttachmentLabels = attachmentNames.length > 0;

    if ((!storedAttachmentPayload || storedAttachmentPayload.length === 0) && hasAttachmentLabels) {
      try {
        const persistedPayload = await fetchStoredAttachmentsForMessage(messageToResubmit.id);
        if (persistedPayload && persistedPayload.length > 0) {
          storedAttachmentPayload = persistedPayload;
          attachmentPayloadByMessageIdRef.current.set(messageToResubmit.id, persistedPayload);
        }
      } catch (loadAttachmentError) {
        console.warn('Failed to load persisted message attachments for resubmit:', loadAttachmentError);
      }
    }
    const hasStoredAttachmentPayload = !!storedAttachmentPayload && storedAttachmentPayload.length > 0;
    const messageContentForResubmit = hasAttachmentLabels ? mainContent : messageToResubmit.content;

    // Get all message IDs to delete (this message and all after it)
    const messagesToDelete = messages.slice(messageIndex);
    const messageIdsToDelete = messagesToDelete
      .map(m => m.id)
      .filter(id => !id.startsWith('temp-')); // Don't try to delete temp messages

    try {
      const attachmentsForDeletedMessages = await fetchAttachmentRowsForMessages(messageIdsToDelete);

      // Delete messages from database
      if (messageIdsToDelete.length > 0) {
        // Use in operator for multiple IDs: id=in.(id1,id2,id3)
        const { error: deleteError } = await dbDelete('messages',
          { id: `in.(${messageIdsToDelete.join(',')})` },
          { token: session.access_token }
        );

        if (deleteError) {
          console.error('Failed to delete messages:', deleteError);
          return { error: 'Failed to delete messages' };
        }
      }

      // Drop in-memory attachment payloads for deleted messages.
      for (const id of messageIdsToDelete) {
        attachmentPayloadByMessageIdRef.current.delete(id);
      }
      // Delete persisted attachment objects for removed messages.
      await deleteStoredAttachmentObjects(attachmentsForDeletedMessages);

      // Update local state to remove deleted messages
      setMessages(prev => prev.slice(0, messageIndex));

      // Now send the message again
      return sendMessage(
        messageContentForResubmit,
        model,
        undefined,
        hasStoredAttachmentPayload ? storedAttachmentPayload : undefined
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resubmit message';
      setError(errorMessage);
      return { error: errorMessage };
    }
  };

  return {
    messages,
    loading,
    sending,
    error,
    streamingContent,
    streamingStatus,
    sendMessage,
    stopLatestRun,
    resubmitMessage,
    lastModifiedFiles,
    clearLastModifiedFiles,
    buildStarted,
    clearBuildStarted,
    modifiedSiblingProjectIds,
    clearModifiedSiblingProjectIds,
  };
}

export function usePublicProject(publicId: string) {
  const { user, session } = useAuth();
  const [project, setProject] = useState<Pick<Project, 'id' | 'name' | 'description' | 'slug' | 'is_published' | 'created_at' | 'updated_at' | 'required_tier' | 'monetization_mode' | 'requires_auth' | 'allow_guest_access' | 'family_params'> & { public_id?: string; user_id?: string; creator_display_name?: string | null; creator_username?: string | null; creator_is_public_profile?: boolean } | null>(null);
  const [frontendHtml, setFrontendHtml] = useState<string | null>(null);
  const [frontendFiles, setFrontendFiles] = useState<Record<string, string>>({});

  // Stable setters: skip setState when content hasn't changed to avoid
  // creating new object references that trigger downstream iframe rebuilds.
  const setFrontendHtmlStable = (val: string | null) => {
    setFrontendHtml(prev => prev === val ? prev : val);
  };
  const setFrontendFilesStable = (val: Record<string, string>) => {
    setFrontendFiles(prev => {
      const prevKeys = Object.keys(prev);
      const newKeys = Object.keys(val);
      if (prevKeys.length !== newKeys.length) return val;
      for (const k of prevKeys) {
        if (prev[k] !== val[k]) return val;
      }
      return prev;
    });
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fork-related state
  const [userFork, setUserFork] = useState<{ id: string; name: string } | null>(null);
  const [showingFork, setShowingFork] = useState(false);
  const [forkedFrontendHtml, setForkedFrontendHtml] = useState<string | null>(null);
  const [forkedFrontendFiles, setForkedFrontendFiles] = useState<Record<string, string>>({});

  const setForkedFrontendHtmlStable = (val: string | null) => {
    setForkedFrontendHtml(prev => prev === val ? prev : val);
  };
  const setForkedFrontendFilesStable = (val: Record<string, string>) => {
    setForkedFrontendFiles(prev => {
      const prevKeys = Object.keys(prev);
      const newKeys = Object.keys(val);
      if (prevKeys.length !== newKeys.length) return val;
      for (const k of prevKeys) {
        if (prev[k] !== val[k]) return val;
      }
      return prev;
    });
  };

  // Helper to fetch code for a project
  const fetchProjectCode = async (projectId: string, needsAuth: boolean = false) => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const headers: Record<string, string> = {};

    if (needsAuth && session) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    console.log('[fetchProjectCode] Fetching:', { projectId, needsAuth, hasAuth: !!headers['Authorization'] });

    const bootstrapResponse = await fetch(
      `${supabaseUrl}/functions/v1/project-code?projectId=${projectId}&bootstrap=1&_t=${Date.now()}`,
      { headers }
    );

    console.log('[fetchProjectCode] Bootstrap response status:', bootstrapResponse.status);

    if (bootstrapResponse.ok) {
      const result = await bootstrapResponse.json();
      console.log('[fetchProjectCode] Bootstrap result keys:', Object.keys(result));

      // Handle both response formats:
      // - Authenticated path returns { files: { ... } }
      // - Unauthenticated path returns { ... } directly
      const files = result.files || result;
      console.log('[fetchProjectCode] Files keys:', Object.keys(files));

      const frontendFile = files['frontend/index.html'] || files['index.html'];

      const otherFiles: Record<string, string> = {};
      for (const [path, content] of Object.entries(files)) {
        if (path.startsWith('frontend/') && path !== 'frontend/index.html' && path !== 'frontend/common.css') {
          const filename = path.replace('frontend/', '');
          otherFiles[filename] = content as string;
        }
      }

      console.log('[fetchProjectCode] Result:', {
        hasFrontendFile: !!frontendFile,
        frontendFileLength: frontendFile ? (frontendFile as string).length : 0,
        otherFilesCount: Object.keys(otherFiles).length
      });

      return { frontendHtml: frontendFile || null, frontendFiles: otherFiles };
    }

    // Log error response
    try {
      const errorData = await bootstrapResponse.json();
      console.error('[fetchProjectCode] Bootstrap error response:', errorData);
    } catch {
      console.error('[fetchProjectCode] Failed to parse bootstrap error response');
    }

    return { frontendHtml: null, frontendFiles: {} };
  };

  useEffect(() => {
    const fetchPublicProject = async () => {
      try {
        setLoading(true);

        // Query the project by public_id - must be published
        const { data: projectData, error: projectError } = await dbSelect<{
          id: string;
          name: string;
          description: string | null;
          slug: string | null;
          is_published: boolean;
          created_at: string;
          updated_at: string;
          public_id: string;
          required_tier: string | null;
          monetization_mode: string;
          requires_auth: boolean;
          allow_guest_access: boolean;
          family_params: Record<string, string> | null;
          user_id: string;
          profiles: { display_name: string | null; username: string | null; is_public_profile: boolean | null } | null;
        }>('projects', {
          select: 'id,name,description,slug,is_published,created_at,updated_at,public_id,required_tier,monetization_mode,requires_auth,allow_guest_access,family_params,user_id,profiles!projects_user_id_fkey(display_name,username,is_public_profile)',
          filters: { public_id: `eq.${publicId}`, is_published: 'eq.true' },
          single: true,
        });

        if (projectError || !projectData) {
          throw new Error('Mini-App not found or not published');
        }

        // Handle profiles join - Supabase may return as array or object
        const profilesData = Array.isArray(projectData.profiles)
          ? projectData.profiles[0]
          : projectData.profiles;

        setProject({
          ...projectData,
          required_tier: projectData.required_tier as Project['required_tier'],
          monetization_mode: (projectData.monetization_mode || 'free') as Project['monetization_mode'],
          requires_auth: projectData.requires_auth || false,
          allow_guest_access: projectData.allow_guest_access || false,
          family_params: projectData.family_params || null,
          user_id: projectData.user_id,
          creator_display_name: profilesData?.display_name || null,
          creator_username: profilesData?.username || null,
          creator_is_public_profile: profilesData?.is_public_profile === true,
        });

        // Fetch original project's code
        console.log('[usePublicProject] Fetching original project code:', projectData.id);
        const originalCode = await fetchProjectCode(projectData.id);
        console.log('[usePublicProject] Original code result:', {
          hasFrontendHtml: !!originalCode.frontendHtml,
          frontendFilesCount: Object.keys(originalCode.frontendFiles).length
        });
        setFrontendHtmlStable(originalCode.frontendHtml);
        setFrontendFilesStable(originalCode.frontendFiles);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch project');
      } finally {
        setLoading(false);
      }
    };

    if (publicId) {
      fetchPublicProject();
    }
  }, [publicId]);

  // Separate effect: check for user's fork when auth becomes available
  // This runs WITHOUT setting loading=true, so FrontendPreview stays mounted
  useEffect(() => {
    const checkForFork = async () => {
      if (!user || !session || !project) return;

      try {
        console.log('[usePublicProject] User logged in, checking for fork...');
        const { data: forkData } = await dbSelect<{ id: string; name: string }>('projects', {
          token: session.access_token,
          select: 'id,name',
          filters: { user_id: `eq.${user.id}`, forked_from: `eq.${project.id}` },
          maybeSingle: true,
        });

        console.log('[usePublicProject] Fork query result:', { forkData });

        if (forkData) {
          setUserFork(forkData);

          // Fetch forked project's code (needs auth since fork might not be published)
          console.log('[usePublicProject] Fetching forked project code:', forkData.id);
          const forkedCode = await fetchProjectCode(forkData.id, true);
          console.log('[usePublicProject] Forked code result:', {
            hasFrontendHtml: !!forkedCode.frontendHtml,
            frontendFilesCount: Object.keys(forkedCode.frontendFiles).length
          });
          setForkedFrontendHtmlStable(forkedCode.frontendHtml);
          setForkedFrontendFilesStable(forkedCode.frontendFiles);

          // Only show fork by default if it has a frontend
          // Otherwise, user can still toggle to see the fork (which will show original's frontend)
          if (forkedCode.frontendHtml) {
            setShowingFork(true);
          }
        }
      } catch (err) {
        console.error('[usePublicProject] Fork check error:', err);
      }
    };

    checkForFork();
  }, [user, session, project?.id]);

  // Return the appropriate frontend based on whether showing fork or original
  // Fall back to original if fork has no frontend (e.g., if file copy failed)
  const forkHasFrontend = !!forkedFrontendHtml;
  const activeFrontendHtmlRaw = (showingFork && userFork && forkHasFrontend) ? forkedFrontendHtml : frontendHtml;
  const activeFrontendFilesRaw = (showingFork && userFork && forkHasFrontend) ? forkedFrontendFiles : frontendFiles;
  const activeProjectId = (showingFork && userFork) ? userFork.id : project?.id;

  // Stabilize references: only update when content actually changes.
  // This prevents iframe rebuilds when switching between state variables
  // (e.g. frontendFiles → forkedFrontendFiles) that have identical content.
  const stableHtmlRef = useRef(activeFrontendHtmlRaw);
  if (stableHtmlRef.current !== activeFrontendHtmlRaw) {
    stableHtmlRef.current = activeFrontendHtmlRaw;
  }
  const activeFrontendHtml = stableHtmlRef.current;

  const stableFilesRef = useRef(activeFrontendFilesRaw);
  const prevFilesKeys = Object.keys(stableFilesRef.current);
  const newFilesKeys = Object.keys(activeFrontendFilesRaw);
  let filesContentChanged = prevFilesKeys.length !== newFilesKeys.length;
  if (!filesContentChanged) {
    for (const k of prevFilesKeys) {
      if (stableFilesRef.current[k] !== activeFrontendFilesRaw[k]) {
        filesContentChanged = true;
        break;
      }
    }
  }
  if (filesContentChanged) {
    stableFilesRef.current = activeFrontendFilesRaw;
  }
  const activeFrontendFiles = stableFilesRef.current;

  // Toggle guest access for the project (admin only)
  const toggleGuestAccess = useCallback(async (enabled: boolean) => {
    if (!project?.id || !session) {
      return { error: 'Not authenticated or no project' };
    }

    const { data, error: updateError } = await dbUpdate<Project>(
      'projects',
      { allow_guest_access: enabled },
      { id: `eq.${project.id}` },
      { token: session.access_token }
    );

    if (updateError) {
      return { error: updateError.message };
    }

    if (data) {
      setProject(prev => prev ? { ...prev, allow_guest_access: enabled } : null);
    }

    return { data };
  }, [project?.id, session]);

  return {
    project,
    frontendHtml: activeFrontendHtml,
    frontendFiles: activeFrontendFiles,
    loading,
    error,
    // Fork-related returns
    userFork,
    showingFork,
    setShowingFork,
    activeProjectId,
    originalFrontendHtml: frontendHtml,
    originalFrontendFiles: frontendFiles,
    // Admin controls
    toggleGuestAccess,
  };
}

interface UseProjectCodeOptions {
  enabled?: boolean;
}

export function useProjectCode(projectId: string | undefined, options?: UseProjectCodeOptions) {
  const { session, authReady, ensureFreshToken } = useAuth();
  const enabled = options?.enabled ?? true;
  const [files, setFiles] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // Use ref for session so fetchCode doesn't re-create when session changes
  // (Supabase fires multiple auth events: INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED)
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const ensureFreshTokenRef = useRef(ensureFreshToken);
  useEffect(() => {
    ensureFreshTokenRef.current = ensureFreshToken;
  }, [ensureFreshToken]);

  const sessionUserId = session?.user?.id;
  useEffect(() => {
    setAccessDenied(false);
  }, [projectId, sessionUserId]);

  // Stable setter: only update state when content actually changes
  const setFilesStable = useCallback((newFiles: Record<string, string>) => {
    setFiles(prev => {
      const prevKeys = Object.keys(prev);
      const newKeys = Object.keys(newFiles);
      if (prevKeys.length !== newKeys.length) return newFiles;
      for (const k of prevKeys) {
        if (prev[k] !== newFiles[k]) return newFiles;
      }
      return prev;
    });
  }, []);

  const fetchCode = useCallback(async (options?: { force?: boolean }) => {
    if (!projectId || !enabled) {
      setFiles({});
      setLoading(false);
      return;
    }
    if (accessDenied && !options?.force) {
      return;
    }

    const currentSession = sessionRef.current;
    if (!currentSession) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const fetchWithAuthRetry = async (requestUrl: string, token: string): Promise<Response> => {
        let response = await fetch(requestUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        // Session may be stale during auth bootstrap or after refresh lag.
        // Refresh once and retry only for auth failures (401).
        if (response.status === 401) {
          const refreshed = await ensureFreshTokenRef.current({ forceRefresh: true });
          if (refreshed) {
            response = await fetch(requestUrl, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${refreshed}`,
              },
            });
          }
        }

        return response;
      };

      const readFilesPayload = async (response: Response): Promise<Record<string, string>> => {
        const result = await response.json();
        return result.files || result || {};
      };

      const fullUrl = `${supabaseUrl}/functions/v1/project-code?projectId=${projectId}&_t=${Date.now()}`;
      const bootstrapUrl = `${supabaseUrl}/functions/v1/project-code?projectId=${projectId}&bootstrap=1&_t=${Date.now()}`;

      // Fast path: fetch only frontend/index.html first so preview can render immediately.
      // Forced refresh skips bootstrap and goes straight to full file set.
      if (!options?.force) {
        const bootstrapResponse = await fetchWithAuthRetry(bootstrapUrl, currentSession.access_token);

        if (bootstrapResponse.status === 403) {
          setAccessDenied(true);
          setFiles({});
          setError('Access denied');
          return;
        }

        if (bootstrapResponse.ok) {
          const bootstrapFiles = await readFilesPayload(bootstrapResponse);
          setFilesStable(bootstrapFiles);
          setAccessDenied(false);
          setLoading(false);

          // Hydrate full code set in background (editor can update once available).
          void (async () => {
            try {
              const fullResponse = await fetchWithAuthRetry(fullUrl, currentSession.access_token);
              if (fullResponse.status === 403) {
                return;
              }
              if (!fullResponse.ok) {
                return;
              }
              const allFiles = await readFilesPayload(fullResponse);
              setFilesStable(allFiles);
            } catch {
              // Keep bootstrap payload if background hydration fails.
            }
          })();
          return;
        }
      }

      const fullResponse = await fetchWithAuthRetry(fullUrl, currentSession.access_token);

      if (fullResponse.status === 403) {
        setAccessDenied(true);
        setFiles({});
        setError('Access denied');
        return;
      }

      if (!fullResponse.ok) {
        const errorData = await fullResponse.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to fetch code');
      }

      const allFiles = await readFilesPayload(fullResponse);
      setFilesStable(allFiles);
      setAccessDenied(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch code');
    } finally {
      setLoading(false);
    }
  }, [projectId, setFilesStable, accessDenied, enabled]);

  // Re-fetch when project/auth identity changes so we don't get stuck with
  // a stale token/user context that can produce 403 responses.
  const hasSession = !!session?.access_token;
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (authReady && hasSession) {
      fetchCode();
    }
  }, [fetchCode, authReady, hasSession, sessionUserId, enabled]);

  const saveFile = async (filename: string, content: string) => {
    if (!projectId) return { error: 'No project ID' };
    if (!session) return { error: 'Not authenticated' };

    try {
      setSaving(true);
      setError(null);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/project-code?projectId=${projectId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'save',
            filename,
            content,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save file');
      }

      const result = await response.json().catch(() => ({} as { buildTriggered?: boolean; buildStatus?: string }));

      // Update local state
      setFiles((prev) => ({ ...prev, [filename]: content }));
      return {
        success: true,
        buildTriggered: result.buildTriggered === true,
        buildStatus: typeof result.buildStatus === 'string' ? result.buildStatus : null,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save file';
      setError(errorMessage);
      return { error: errorMessage };
    } finally {
      setSaving(false);
    }
  };

  const createFile = async (filename: string, content: string = '') => {
    return saveFile(filename, content);
  };

  const deleteFile = async (filename: string) => {
    if (!projectId) return { error: 'No project ID' };
    if (!session) return { error: 'Not authenticated' };

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/project-code?projectId=${projectId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'delete',
            filename,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete file');
      }

      // Update local state
      setFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[filename];
        return newFiles;
      });
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete file';
      setError(errorMessage);
      return { error: errorMessage };
    }
  };

  return {
    files,
    loading,
    saving,
    error,
    fetchCode,
    saveFile,
    createFile,
    deleteFile,
  };
}

// Types for file versions
export interface FileVersion {
  conversation_id: string;
  title: string;
  created_at: string;
  is_manual: boolean;
}

export function useFileVersions(projectId: string | undefined) {
  const { session } = useAuth();
  const [versions, setVersions] = useState<FileVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVersions = useCallback(async (filename: string): Promise<FileVersion[]> => {
    if (!projectId || !filename) {
      setVersions([]);
      return [];
    }

    if (!session) {
      setError('Not authenticated');
      return [];
    }

    try {
      setLoading(true);
      setError(null);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/project-code?projectId=${projectId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'list-versions',
            filename,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch versions');
      }

      const result = await response.json();
      const fetchedVersions = result.versions || [];
      setVersions(fetchedVersions);
      return fetchedVersions;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch versions');
      return [];
    } finally {
      setLoading(false);
    }
  }, [projectId, session]);

  const getVersionContent = async (filename: string, conversationId: string): Promise<{ content: string } | { error: string }> => {
    if (!projectId) return { error: 'No project ID' };
    if (!session) return { error: 'Not authenticated' };

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/project-code?projectId=${projectId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'get-version',
            filename,
            conversationId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.error || 'Failed to get version' };
      }

      const result = await response.json();
      return { content: result.content };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Failed to get version' };
    }
  };

  const restoreFile = async (filename: string, conversationId: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    if (!projectId) return { success: false, error: 'No project ID' };
    if (!session) return { success: false, error: 'Not authenticated' };

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/project-code?projectId=${projectId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'restore-file',
            filename,
            conversationId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, error: errorData.error || 'Failed to restore file' };
      }

      const result = await response.json();
      return { success: true, message: result.message };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to restore file' };
    }
  };

  const rollbackToConversation = async (conversationId: string): Promise<{ success: boolean; message?: string; files?: string[]; error?: string }> => {
    if (!projectId) return { success: false, error: 'No project ID' };
    if (!session) return { success: false, error: 'Not authenticated' };

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/project-code?projectId=${projectId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'rollback',
            conversationId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, error: errorData.error || 'Failed to rollback' };
      }

      const result = await response.json();
      return { success: true, message: result.message, files: result.files };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to rollback' };
    }
  };

  const checkConversationHasVersions = useCallback(async (_conversationId: string): Promise<boolean> => {
    // File versions are now stored in Supabase Storage, not in a database table.
    // This function is kept for API compatibility but always returns false.
    // TODO: Implement storage-based version checking if needed.
    return false;
  }, [projectId]);

  return {
    versions,
    loading,
    error,
    fetchVersions,
    getVersionContent,
    restoreFile,
    rollbackToConversation,
    checkConversationHasVersions,
  };
}
